// Stage 1, scenario (spec §2). Four areas of operations as cards; choose one and the same scenario
// template — a task force attacks to seize an objective while the enemy defends in depth — is fitted
// onto that ground and handed to you as an OPORD (operation order) in cards rather than a wall of text.
//
// The one estimate in the order carries an "S-2 ASSESSMENT" chip (S-2 is the intelligence officer), so
// a planner can always tell what is known from what is guessed (spec §7).

import { useEffect, useMemo, useState } from 'react';
import { useStore } from '../../state/store';
import { UNIT_DEFS } from '../../sim/scenario';
import type { AoMeta, Dials } from '../../sim/types';
import { AoCard, type AoCardData } from '../cards/AoCard';
import { DIALS, DialGroup } from '../cards/DialChip';
import { Chip } from '../cards/ui';
import { Acronym, AcronymText } from '../Acronym';
import { Wordmark } from '../Wordmark';
import { buildOpord, type Opord } from '../opord';
import { symbolUrl } from '../symbol';
import { InstructorPanel, rulesetLabel } from '../InstructorPanel';

/** The four areas of operations the terrain pipeline has built (spec §3). */
const AO_NAMES = ['ntc', 'hohenfels', 'suwalki', 'cheorwon'] as const;

export function ScenarioStage() {
  const scenario = useStore((s) => s.scenario);
  const meta = useStore((s) => s.meta);
  // The OPORD is the last card set of stage 1: the area of operations is loaded and fitted, but no
  // simulation exists yet and the planning clock has not started.
  if (scenario && meta) return <OpordView />;
  return <AoGrid />;
}

// ---- the area-of-operations grid -------------------------------------------------------------------

function AoGrid() {
  const chooseAo = useStore((s) => s.chooseAo);
  const quickStart = useStore((s) => s.quickStart);
  const setStage = useStore((s) => s.setStage);
  const loading = useStore((s) => s.loading);
  const error = useStore((s) => s.error);
  const [cards, setCards] = useState<AoCardData[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const loaded = await Promise.all(
          AO_NAMES.map(async (name) => {
            const [metaRes, costRes] = await Promise.all([
              fetch(`/ao/${name}/meta.json`),
              fetch(`/ao/${name}/cost.bin`),
            ]);
            if (!metaRes.ok) throw new Error(`${name}/meta.json: ${metaRes.status}`);
            if (!costRes.ok) throw new Error(`${name}/cost.bin: ${costRes.status}`);
            const meta = (await metaRes.json()) as AoMeta;
            const cost = new Uint8Array(await costRes.arrayBuffer());
            return { name, meta, cost } satisfies AoCardData;
          }),
        );
        if (!cancelled) setCards(loaded);
      } catch (e) {
        if (!cancelled) setLoadError(e instanceof Error ? e.message : String(e));
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <div data-testid="scenario-stage" className="thin-scroll h-full overflow-y-auto bg-ink">
      <div className="mx-auto max-w-[1080px] px-6 py-8">
        <Wordmark size="page" />
        <h1 className="mt-2 text-[24px] text-paper">Choose your area of operations</h1>
        <p className="mt-2 max-w-[70ch] text-[13px] leading-relaxed text-fog">
          Four pieces of real ground, built from public elevation, land-cover and road data. The same
          scenario is fitted onto whichever you pick: your task force attacks from an assembly area on
          one edge to seize an objective on the far side, while the enemy defends in depth between the
          two. The terrain decides everything else.
        </p>

        {loadError && <p className="mono mt-4 text-[11px] text-hostile">terrain: {loadError}</p>}
        {error && <p className="mono mt-4 text-[11px] text-hostile">{error}</p>}

        <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
          {cards.length === 0 && !loadError
            ? AO_NAMES.map((name) => (
                <div key={name} className="h-[280px] animate-pulse rounded-sm border border-slate-700 bg-slate-800/40" />
              ))
            : cards.map((data) => (
                <AoCard
                  key={data.name}
                  data={data}
                  disabled={loading}
                  onChoose={() => void chooseAo(data.name)}
                />
              ))}
        </div>

        <div className="mt-6 flex items-center gap-3 border-t border-slate-700 pt-3">
          <span className="mono text-[10px] uppercase tracking-[0.2em] text-fog">
            {loading ? 'loading terrain…' : 'shortcut'}
          </span>
          {/* The checkpoint-2 path: straight into the fight, no order and no planning clock. Kept small
              and secondary — it is a development shortcut, and the end-to-end smoke test's fast lane. */}
          <button
            type="button"
            data-testid="quick-start"
            disabled={loading}
            onClick={() => void quickStart('ntc')}
            className="mono text-[11px] text-fog underline decoration-dotted underline-offset-4 hover:text-[var(--color-friendly)]"
          >
            Quick start on the NTC (National Training Center) corridor — skip the order and the planning clock
          </button>
        </div>

        <footer className="mt-8 border-t border-slate-800 pt-4">
          <button
            type="button"
            data-testid="design-record-link"
            onClick={() => setStage('design')}
            className="mono text-[10px] uppercase tracking-[0.2em] text-fog underline decoration-dotted underline-offset-4 hover:text-[var(--color-friendly)]"
            title="How this planning loop maps to WINDTUNNEL, the AI (artificial intelligence) pattern decisions, and the trade-off log"
          >
            Design record
          </button>
        </footer>
      </div>
    </div>
  );
}

// ---- the OPORD --------------------------------------------------------------------------------------

function OpordView() {
  const scenario = useStore((s) => s.scenario)!;
  const meta = useStore((s) => s.meta)!;
  const grid = useStore((s) => s.grid);
  const dials = useStore((s) => s.dials);
  const setDials = useStore((s) => s.setDials);
  const beginPlanning = useStore((s) => s.beginPlanning);
  const setStage = useStore((s) => s.setStage);
  const [showInstructor, setShowInstructor] = useState(false);

  const opord = useMemo<Opord>(
    () => buildOpord(scenario, meta, dials, grid?.cost),
    [scenario, meta, dials, grid],
  );

  return (
    <div data-testid="opord" className="thin-scroll h-full overflow-y-auto bg-ink">
      <header className="sticky top-0 z-10 flex items-center gap-3 border-b border-slate-700 bg-slate-900/95 px-6 py-3 backdrop-blur-sm">
        <Wordmark />
        <span className="mono text-[13px] tracking-[0.1em] text-paper">
          <AcronymText>{opord.heading}</AcronymText>
        </span>
        <span className="mono text-[11px] text-fog">{opord.aoTitle}</span>
        <div className="ml-auto flex items-center gap-2">
          <Chip
            onClick={() => {
              // Back to the grid: drop the fitted scenario so stage 1 shows the cards again.
              useStore.setState({ scenario: null, meta: null, grid: null, buffers: null });
              setStage('scenario');
            }}
            testId="opord-back"
            title="Pick a different area of operations"
          >
            ← areas of operations
          </Chip>
          <button
            type="button"
            data-testid="begin-planning"
            onClick={beginPlanning}
            className="mono rounded-sm border px-3 py-1.5 text-[11px] uppercase tracking-[0.2em]"
            style={{
              borderColor: 'var(--color-friendly)',
              color: 'var(--color-friendly)',
              background: 'color-mix(in srgb, var(--color-friendly) 12%, transparent)',
            }}
            title={`Start the planning clock: ${dials.planMin} real minutes to draw the course of action and task the force`}
          >
            Begin planning →
          </button>
        </div>
      </header>

      <div className="mx-auto max-w-[1080px] space-y-4 px-6 py-6">
        <OpordCard n="1" title="Situation">
          <p className="text-[13px] leading-relaxed text-paper"><AcronymText>{opord.situation.enemy}</AcronymText></p>
          <p className="mt-2 text-[13px] leading-relaxed text-paper"><AcronymText>{opord.situation.posture}</AcronymText></p>
          {opord.situation.terrain && (
            <p className="mt-2 text-[12px] leading-relaxed text-fog">{opord.situation.terrain}</p>
          )}
          <div
            data-testid="s2-assessment"
            className="mt-3 rounded-sm border-l-2 bg-slate-800/60 px-3 py-2"
            style={{ borderColor: 'var(--color-warn)' }}
          >
            <Chip tone="warn" active title="An estimate by the S-2, the intelligence officer — not a confirmed report">
              <Acronym k="s-2">S-2</Acronym> assessment
            </Chip>
            <p className="mt-1.5 text-[12px] leading-relaxed text-fog"><AcronymText>{opord.situation.assessment}</AcronymText></p>
          </div>
        </OpordCard>

        <OpordCard n="2" title="Mission">
          <p className="text-[15px] leading-relaxed text-paper"><AcronymText>{opord.mission}</AcronymText></p>
        </OpordCard>

        <OpordCard n="3" title="Commander's intent">
          <div className="grid gap-3 md:grid-cols-3">
            <IntentBlock label="Purpose">
              <p className="text-[12px] leading-relaxed text-paper"><AcronymText>{opord.intent.purpose}</AcronymText></p>
            </IntentBlock>
            <IntentBlock label="Key tasks">
              <ul className="space-y-1.5">
                {opord.intent.keyTasks.map((t) => (
                  <li key={t} className="flex gap-2 text-[12px] leading-relaxed text-paper">
                    <span style={{ color: 'var(--color-friendly)' }}>—</span>
                    <span><AcronymText>{t}</AcronymText></span>
                  </li>
                ))}
              </ul>
            </IntentBlock>
            <IntentBlock label="End state">
              <p className="text-[12px] leading-relaxed text-paper"><AcronymText>{opord.intent.endState}</AcronymText></p>
            </IntentBlock>
          </div>
        </OpordCard>

        <OpordCard n="4" title="Task organization">
          <div className="space-y-3">
            {opord.taskOrg.map((group) => (
              <div key={group.key} data-testid={`taskorg-${group.key}`}>
                <div className="mono text-[10px] uppercase tracking-[0.2em]" style={{ color: 'var(--color-friendly)' }}>
                  {group.title}
                </div>
                <div className="mt-1.5 flex flex-wrap gap-1.5">
                  {group.units.map((u) => (
                    <div
                      key={u.id}
                      className="flex w-[96px] flex-col items-center rounded-sm border border-slate-700 bg-slate-800/60 px-1 py-1.5"
                      title={`${u.callsign} — ${u.label}`}
                    >
                      <img src={symbolUrl(u.sidc, 30)} alt="" width={30} height={30} />
                      <span className="mono mt-0.5 text-[11px] tracking-[0.1em] text-paper">{u.callsign}</span>
                      <span className="truncate text-[9px] text-fog">{UNIT_DEFS[u.type].label}</span>
                    </div>
                  ))}
                </div>
                <p className="mt-1 text-[11px] leading-snug text-fog"><AcronymText>{group.note}</AcronymText></p>
              </div>
            ))}
          </div>
        </OpordCard>

        <OpordCard n="5" title="Dials">
          <div className="flex flex-wrap items-center gap-2">
            <p className="flex-1 text-[12px] leading-relaxed text-fog">
              Six settings decide how hard this run is. Each says what it changes; turn the enemy's posture
              and the fitter lays the enemy out again on the same ground.
            </p>
            <Chip
              onClick={() => setShowInstructor(true)}
              testId="instructor-open"
              title="Open the Instructor panel: presets and sliders for the tuning constants the sim runs on"
            >
              instructor
            </Chip>
            <span className="mono text-[10px] text-fog" data-testid="ruleset-label">
              {rulesetLabel(dials.rules.preset, dials.rules.overrides)}
            </span>
          </div>
          <div className="mt-3 grid gap-2 md:grid-cols-2 lg:grid-cols-3">
            {DIALS.map((spec) => (
              <DialGroup key={spec.key} spec={spec} dials={dials} onPick={(patch: Partial<Dials>) => setDials(patch)} />
            ))}
          </div>
        </OpordCard>
      </div>
      {showInstructor && <InstructorPanel onClose={() => setShowInstructor(false)} />}
    </div>
  );
}

function OpordCard({ n, title, children }: { n: string; title: string; children: React.ReactNode }) {
  return (
    <section
      data-testid={`opord-${title.toLowerCase().replace(/[^a-z]+/g, '-')}`}
      className="rounded-sm border border-slate-700 bg-slate-900/70 p-4"
    >
      <header className="flex items-baseline gap-2 border-b border-slate-700 pb-2">
        <span className="mono text-[11px] text-fog">{n}</span>
        <h2 className="mono text-[12px] uppercase tracking-[0.25em] text-paper">{title}</h2>
      </header>
      <div className="mt-3">{children}</div>
    </section>
  );
}

function IntentBlock({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="rounded-sm border border-slate-700 bg-slate-800/40 p-3">
      <div className="mono text-[9px] uppercase tracking-[0.25em] text-fog">{label}</div>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
